/**
 * Bay Window Cushion Configurator — Pricing Engine + Interactive SVG Preview
 * uscushion.com · 2026-07
 *
 * Bay window cushion = single trapezoid piece
 * Front Length > Back Length, Depth = perpendicular distance, Thickness = foam height
 * Pricing: bench formula with effective length = max(frontL, backL)
 *   (any shape is cut from a rectangle, offcuts are waste)
 *
 * Tuner-confirmed parameters:
 *   origin=[220,240] frontLenPx=200 backLenPx=140
 *   depthPx=80 seatH=14
 *   dirHoriz=[0.98776,-0.15596] dirDiag=[-0.35120,-0.93660]
 */
(function () {
  'use strict';

  /* ─── Cost constants (same as bench) ─── */
  var FABRIC_PRICE_RMB  = 25;
  /* Premium fabric grades (RMB per m2); standard falls back to FABRIC_PRICE_RMB. Driven by fabric `grade:` tag. */
  var FABRIC_RATE_RMB   = { marine: 54 };
  function fabricRate() {
    var g = (state.fabric && state.fabric.tags) ? extractTag(state.fabric.tags, 'grade') : '';
    return FABRIC_RATE_RMB[g] || FABRIC_PRICE_RMB;
  }
  var FOAM_PRICE_RMB    = 1750;
  var FOAM_DENSITY      = 35;
  var FABRIC_GSM        = 400;
  var SHIPPING_RMB      = 90;
  var ACCESSORIES_RMB   = 30;
  var EXCHANGE_RATE     = 6.8;
  var PAYMENT_FEE_RATE  = 0.049;
  var TARGET_COST_RATIO = 0.40;
  var MARINE_COST_RATIO = 0.38;   /* Marine fabric target margin 62% (standard 60%); used when grade:marine is selected */
  var COVER_COST_RATIO  = 0.55;   /* Extra covers = 45% margin (AOV add-on, lower than cushion's 60/62%); same for standard & marine */
  function targetRatio() {
    var g = (state.fabric && state.fabric.tags) ? extractTag(state.fabric.tags, 'grade') : '';
    return g === 'marine' ? MARINE_COST_RATIO : TARGET_COST_RATIO;
  }
  var MIN_PRICE         = 45;

  /* ─── Addon pricing (USD) ─── */
  var ADDON = {
    piping:      { none: 0, single: 5, double: 8 },
    pipingColor: { match: 0, white: 5, black: 5, navy: 5, cream: 5 },
    straps:      { none: 0, two: 2, four: 4 },
    zipper:      { regular: 0, invisible: 0 },
    antislip:    { none: 0, black_pad: 5, beige_pad: 5, velcro: 9 },
    throwPillow: {
      none: 0,
      '2pcs_12x20': 29, '2pcs_16x16': 29, '2pcs_18x18': 32,
      '2pcs_20x20': 35, '2pcs_24x24': 38, '2pcs_custom': 40,
      '4pcs_12x20': 51, '4pcs_16x16': 51, '4pcs_18x18': 57,
      '4pcs_20x20': 63, '4pcs_24x24': 67, '4pcs_custom': 70
    },
    pillowCovers: {
      none: 0,
      '2pcs_12x20': 19, '2pcs_16x16': 19, '2pcs_18x18': 21,
      '2pcs_20x20': 24, '2pcs_24x24': 29, '2pcs_custom': 31,
      '4pcs_12x20': 32, '4pcs_16x16': 32, '4pcs_18x18': 36,
      '4pcs_20x20': 42, '4pcs_24x24': 52, '4pcs_custom': 55
    },
    armrestPillow: { none: 0, square_2pcs: 30, round_2pcs: 30 }
  };

  var LABELS = {
    none: 'None', single: 'Single piping', double: 'Double piping',
    match: 'Match fabric', white: 'White piping', black: 'Black piping', navy: 'Navy piping', cream: 'Cream piping',
    two: '2 straps', four: '4 straps',
    regular: 'Regular zipper', invisible: 'Invisible zipper',
    velcro: 'Velcro', black_pad: 'Black pad', beige_pad: 'Beige pad'
  };

  var FABRIC_COLORS = {
    'blue':'#2c3e6b','navy':'#1a2540','teal':'#2a7a6b','green':'#4a7a2a',
    'sage':'#a8b89a','red':'#b83232','coral':'#d85a45','orange':'#d4783a',
    'yellow':'#d4a83a','gold':'#c8963a','pink':'#d4649a','purple':'#6a4a9a',
    'lavender':'#9a8ab8','brown':'#7a5a3a','camel':'#c8a878','tan':'#c8b08a',
    'beige':'#d4c8a8','cream':'#ede8df','ivory':'#f0ead8','white':'#f5f5f0',
    'gray':'#9a9a9a','grey':'#9a9a9a','charcoal':'#4a4a4a','black':'#1a1a1a',
    'mint':'#98d4b4','rust':'#b7502a','mustard':'#c8a020','olive':'#6b702a',
    'blush':'#de9898','mauve':'#b07898','burgundy':'#6b1a2a','taupe':'#8a7a6a',
    'sand':'#c8b898','silver':'#bfc0c2','steel blue':'#4a7a98',
    'dark olive':'#4a4a2a','dark green':'#2a5a2a','dark brown':'#4a3020',
    'dark blue':'#1a2a5a','dark teal':'#1a5a5a','dark gray':'#5a5a5a',
    'light gray':'#c8c8c8','gray blue':'#6a7a8a','yellow green':'#8ab82a',
    'gray black':'#3a3a3a'
  };

  /* ─── State ─── */
  var state = {
    frontL: 0, backL: 0, depth: 0, thickness: 0,
    fabric: null, piping: '', pipingColor: 'match', straps: '',
    zipper: 'regular', zipperPlacement: '', antislip: '',
    extraCovers: '0', throwPillow: 'none', pillowCovers: 'none',
    armrestPillow: 'none', notes: ''
  };

  var els = {};

  /* ─── Color helpers ─── */
  function hexToRgb(hex) {
    hex = hex.replace('#', '');
    return [parseInt(hex.substring(0, 2), 16), parseInt(hex.substring(2, 4), 16), parseInt(hex.substring(4, 6), 16)];
  }
  function rgbToHex(r, g, b) {
    return '#' + [r, g, b].map(function (c) {
      var x = Math.round(Math.max(0, Math.min(255, c))).toString(16);
      return x.length === 1 ? '0' + x : x;
    }).join('');
  }
  function lighten(hex, r) { var c = hexToRgb(hex); return rgbToHex(c[0] + (255 - c[0]) * r, c[1] + (255 - c[1]) * r, c[2] + (255 - c[2]) * r); }
  function darken(hex, r)  { var c = hexToRgb(hex); return rgbToHex(c[0] * (1 - r), c[1] * (1 - r), c[2] * (1 - r)); }
  function extractTag(tags, prefix) {
    if (!tags) return '';
    for (var i = 0; i < tags.length; i++) {
      var t = (typeof tags[i] === 'string' ? tags[i] : '').toLowerCase().trim();
      if (t.indexOf(prefix + ':') === 0) return tags[i].split(':')[1].trim().toLowerCase();
    }
    return '';
  }

  /* ─── Pricing (bench formula, using average length for trapezoid) ─── */
  function calcBasePrice(L, W, T) {
    if (!L || !W || !T) return null;
    var Lm = L / 39.37, Wm = W / 39.37, Tm = T / 39.37;
    var fabricArea = Lm * Wm * 2 + Lm * Tm * 2 + Wm * Tm * 2;
    var foamVolume = Lm * Wm * Tm;
    var totalWeight = foamVolume * FOAM_DENSITY + fabricArea * FABRIC_GSM / 1000;
    var totalRMB = fabricArea * fabricRate() + foamVolume * FOAM_PRICE_RMB + totalWeight * SHIPPING_RMB + ACCESSORIES_RMB;
    return Math.max(totalRMB / EXCHANGE_RATE / (1 - PAYMENT_FEE_RATE) / targetRatio() * (window.__CC_COVER_FACTOR || 1), MIN_PRICE);
  }

  /* One extra cover = a fabric-only shell (no foam). Trapezoid's bounding rectangle
     = max(frontL, backL) × depth, matching calcBasePrice. Fabric grade auto via
     fabricRate, at COVER_COST_RATIO (45% margin). Replaces the flat $30/$50 fee. */
  function extraCoverUnit() {
    var L = Math.max(state.frontL, state.backL), W = state.depth, T = state.thickness;
    if (!L || !W || !T) return 0;
    var Lm = L / 39.37, Wm = W / 39.37, Tm = T / 39.37;
    var fabricArea = Lm * Wm * 2 + Lm * Tm * 2 + Wm * Tm * 2;
    var weight = fabricArea * FABRIC_GSM / 1000; // fabric weight only — no foam
    var totalRMB = fabricArea * fabricRate() + weight * SHIPPING_RMB + ACCESSORIES_RMB;
    return Math.round(totalRMB / EXCHANGE_RATE / (1 - PAYMENT_FEE_RATE) / COVER_COST_RATIO);
  }

  function updateExtraCoverLabels() {
    if (!els.extraCovers) return;
    var unit = extraCoverUnit();
    var opts = els.extraCovers.options;
    for (var i = 0; i < opts.length; i++) {
      var n = parseInt(opts[i].value) || 0;
      if (n === 0) { opts[i].text = 'No extra cover'; continue; }
      var label = (n === 1 ? '1 set' : n + ' sets');
      opts[i].text = unit > 0 ? label + ' (+$' + (unit * n) + ')' : label;
    }
  }

  function getAddonTotal() {
    return (ADDON.piping[state.piping] || 0)
      + (ADDON.pipingColor[state.pipingColor] || 0)
      + (ADDON.straps[state.straps] || 0) + (ADDON.zipper[state.zipper] || 0)
      + (ADDON.antislip[state.antislip] || 0) + extraCoverUnit() * (parseInt(state.extraCovers) || 0)
      + (ADDON.throwPillow[state.throwPillow] || 0)
      + (ADDON.pillowCovers[state.pillowCovers] || 0)
      + (ADDON.armrestPillow[state.armrestPillow] || 0);
  }

  function hasDims() {
    return state.frontL >= 6 && state.backL >= 6 && state.depth >= 6 && state.thickness >= 1;
  }

  function isComplete() {
    return hasDims()
      && state.fabric && state.piping && state.straps && state.antislip;
  }

  /* ═══════════════════════════════════════════════════════════
     SVG Preview — Trapezoid bay window cushion
     Uses same isometric axes as RV configurator for consistency:
       dirLen   = [0.98776, -0.15596]
       dirWidth = [-0.35120, -0.93660]
     Symmetric trapezoid: back edge centered, shorter than front
     ═══════════════════════════════════════════════════════════ */

  function drawCushion() {
    var svg = els.dimSvg;
    if (!svg) return;

    var fL = state.frontL, bkL = state.backL, dp = state.depth, tk = state.thickness;
    var empty = (!fL && !bkL && !dp && !tk);

    function clamp(v, mn, mx) { return Math.max(mn, Math.min(mx, v)); }
    function pt(a) { return a[0].toFixed(1) + ',' + a[1].toFixed(1); }
    function f(n)  { return n.toFixed(1); }

    /* ── Resolve face colors from fabric ── */
    var topC = '#EDE9E0', frontC = '#D5D0C2', sideC = '#E4E0D4', strokeC = '#2A2A28';
    var fabricLum = 200;
    if (state.fabric && state.fabric.tags) {
      var colorTag = extractTag(state.fabric.tags, 'color');
      var baseHex = FABRIC_COLORS[colorTag];
      if (baseHex) {
        topC = lighten(baseHex, 0.50);
        sideC = lighten(baseHex, 0.35);
        frontC = lighten(baseHex, 0.20);
        strokeC = darken(baseHex, 0.45);
        var sc = hexToRgb(strokeC);
        fabricLum = (sc[0] + sc[1] + sc[2]) / 3;
        if (fabricLum > 120) strokeC = '#2A2A28';
        var fc = hexToRgb(baseHex);
        fabricLum = (fc[0] + fc[1] + fc[2]) / 3;
      }
    }

    var h = '';

    /* ── Defs ── */
    h += '<defs><marker id="bw-arr" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse">'
      + '<path d="M1 1.5L8.5 5L1 8.5" fill="none" stroke="context-stroke" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></marker></defs>';

    /* ── Isometric direction vectors (same as RV configurator) ── */
    var dirLen   = [0.98776, -0.15596];
    var dirWidth = [-0.35120, -0.93660];

    /* ── Compute pixel sizes ── */
    var frontLenPx, backLenPx, depthPx, seatH;
    if (empty) {
      frontLenPx = 270; backLenPx = 175; depthPx = 85; seatH = 18;
    } else {
      var uFL = fL || 72, uBL = bkL || 48, uDp = dp || 18, uTk = tk || 3;
      var lenScale = clamp(270 / Math.max(uFL, uBL), 1.5, 5);
      frontLenPx = clamp(uFL * lenScale, 100, 380);
      backLenPx  = clamp(uBL * lenScale, 60, 350);
      depthPx    = clamp(uDp * lenScale * 1.25, 30, 120);
      seatH      = clamp(uTk * 6, 8, 40);
    }

    /* ── Corners — symmetric trapezoid ──
       顶面4个角:  flPt(前左) frPt(前右) blPt(后左) brPt(后右)
       底面4个角:  flPtB frPtB blPtB brPtB (各顶点垂直下移 seatH)
       中点:       fMid(前边中点) bMid(后边中点)

       俯视布局:
              blPt -------- brPt        ← 后边(短)
             /                  \
           /                      \
         flPt ---------------- frPt    ← 前边(长)
    */
    var origin = [100, 250];
    var flPt = origin;                   // 前左
    var frPt = [flPt[0] + dirLen[0] * frontLenPx, flPt[1] + dirLen[1] * frontLenPx]; // 前右
    var fMid = [(flPt[0] + frPt[0]) / 2, (flPt[1] + frPt[1]) / 2]; // 前边中点
    var bMid = [fMid[0] + dirWidth[0] * depthPx, fMid[1] + dirWidth[1] * depthPx]; // 后边中点
    var blPt = [bMid[0] - dirLen[0] * backLenPx / 2, bMid[1] - dirLen[1] * backLenPx / 2]; // 后左
    var brPt = [bMid[0] + dirLen[0] * backLenPx / 2, bMid[1] + dirLen[1] * backLenPx / 2]; // 后右

    var flPtB = [flPt[0], flPt[1] + seatH]; // 前左底
    var frPtB = [frPt[0], frPt[1] + seatH]; // 前右底
    var blPtB = [blPt[0], blPt[1] + seatH]; // 后左底
    var brPtB = [brPt[0], brPt[1] + seatH]; // 后右底

    /* ── Auto-fit viewBox ── */
    var allPts = [flPt, frPt, blPt, brPt, flPtB, frPtB, blPtB, brPtB];
    var minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    for (var i = 0; i < allPts.length; i++) {
      if (allPts[i][0] < minX) minX = allPts[i][0];
      if (allPts[i][0] > maxX) maxX = allPts[i][0];
      if (allPts[i][1] < minY) minY = allPts[i][1];
      if (allPts[i][1] > maxY) maxY = allPts[i][1];
    }
    var mrgL = 70, mrgR = 55, mrgT = 35, mrgB = 45;
    var vw = Math.ceil(maxX - minX + mrgL + mrgR);
    var vh = Math.ceil(maxY - minY + mrgT + mrgB);
    var vx = Math.floor(minX - mrgL);
    var vy = Math.floor(minY - mrgT);
    svg.setAttribute('viewBox', vx + ' ' + vy + ' ' + vw + ' ' + vh);

    /* ── Straps (drawn behind cushion) ── */
    if ((state.straps === 'two' || state.straps === 'four') && !empty) {
      var tieC = fabricLum < 100 ? '#8A8A85' : '#4A4A47';
      var hangLen = 16;
      function drawTies(cx_, cy_, dirX) {
        var spread = 4;
        h += '<path d="M' + f(cx_ - spread * 0.5) + ' ' + f(cy_)
          + ' Q' + f(cx_ - spread + dirX * 3) + ' ' + f(cy_ + hangLen * 0.5)
          + ' ' + f(cx_ - spread * 1.5 + dirX * 5) + ' ' + f(cy_ + hangLen)
          + '" fill="none" stroke="' + tieC + '" stroke-width="1.6" stroke-linecap="round"/>';
        h += '<path d="M' + f(cx_ + spread * 0.5) + ' ' + f(cy_)
          + ' Q' + f(cx_ + spread + dirX * 2) + ' ' + f(cy_ + hangLen * 0.5)
          + ' ' + f(cx_ + spread * 1.5 + dirX * 4) + ' ' + f(cy_ + hangLen)
          + '" fill="none" stroke="' + tieC + '" stroke-width="1.6" stroke-linecap="round"/>';
      }
      drawTies(flPtB[0], flPtB[1], -1);
      drawTies(frPtB[0], frPtB[1], 1);
      if (state.straps === 'four') {
        drawTies(blPtB[0], blPtB[1], -1);
        drawTies(brPtB[0], brPtB[1], 1);
      }
    }

    /* ── Main faces ──
       Face A (座面/Top):     blPt→brPt→frPt→flPt  — 俯视看到的梯形顶面
       Face B (前面/Front):   flPt→frPt→frPtB→flPtB — 正前方看到的竖面（厚度）
       Face C (左侧/Left):   flPt→blPt→blPtB→flPtB — 左边的斜面
       Face D (右侧/Right):  frPt→brPt→brPtB→frPtB — 右边的斜面
       绘制顺序: D(最远) → C(左后) → A(顶) → B(最前)
    */
    // Face D — 右侧 (Right side) — drawn first (farthest back)
    if (Math.abs(frPt[0] - brPt[0]) > 5 || Math.abs(frPt[1] - brPt[1]) > 5) {
      h += '<polygon points="' + [frPt, brPt, brPtB, frPtB].map(pt).join(' ') + '" fill="' + sideC + '" stroke="' + strokeC + '" stroke-width="1.4" stroke-linejoin="round"/>';
    }
    // Face C — 左侧 (Left side)
    h += '<polygon points="' + [flPt, blPt, blPtB, flPtB].map(pt).join(' ') + '" fill="' + sideC + '" stroke="' + strokeC + '" stroke-width="1.4" stroke-linejoin="round"/>';
    // Face A — 座面 (Top)
    h += '<polygon points="' + [blPt, brPt, frPt, flPt].map(pt).join(' ') + '" fill="' + topC + '" stroke="' + strokeC + '" stroke-width="1.4" stroke-linejoin="round"/>';
    // Face B — 前面 (Front)
    h += '<polygon points="' + [flPt, frPt, frPtB, flPtB].map(pt).join(' ') + '" fill="' + frontC + '" stroke="' + strokeC + '" stroke-width="1.4" stroke-linejoin="round"/>';

    /* ── Piping ── */
    if (state.piping === 'single' || state.piping === 'double') {
      var pipC = '#F5F1E8';
      var pw = 2.2;
      h += '<polyline points="' + [flPt, blPt, brPt, frPt, flPt].map(pt).join(' ')
        + '" fill="none" stroke="' + pipC + '" stroke-width="' + pw + '" stroke-linejoin="round" stroke-linecap="round" opacity="0.85"/>';
      if (state.piping === 'double') {
        h += '<polyline points="' + [blPtB, flPtB, frPtB].map(pt).join(' ')
          + '" fill="none" stroke="' + pipC + '" stroke-width="' + pw + '" stroke-linejoin="round" stroke-linecap="round" opacity="0.85"/>';
      }
    }

    /* ── Anti-slip on bottom ── */
    if (state.antislip && state.antislip !== 'none' && !empty) {
      var padC, padOp;
      if (state.antislip === 'black_pad') { padC = '#1A1A18'; padOp = 0.32; }
      else if (state.antislip === 'beige_pad') { padC = '#C8B898'; padOp = 0.4; }
      else { padC = '#4A4020'; padOp = 0.26; }

      var padOffset = 1, padThick = 4, padInset = 5;
      var normalDir = [-dirLen[1], dirLen[0]];

      function stripQuad(p1, p2, t0, t1) {
        var edgeLen = Math.sqrt(Math.pow(p2[0] - p1[0], 2) + Math.pow(p2[1] - p1[1], 2));
        var q1 = [p1[0] + (p2[0] - p1[0]) * (t0 / edgeLen), p1[1] + (p2[1] - p1[1]) * (t0 / edgeLen)];
        var q2 = [p1[0] + (p2[0] - p1[0]) * (t1 / edgeLen), p1[1] + (p2[1] - p1[1]) * (t1 / edgeLen)];
        var q1o = [q1[0] + normalDir[0] * padOffset, q1[1] + normalDir[1] * padOffset];
        var q2o = [q2[0] + normalDir[0] * padOffset, q2[1] + normalDir[1] * padOffset];
        var q3 = [q2[0] + normalDir[0] * (padOffset + padThick), q2[1] + normalDir[1] * (padOffset + padThick)];
        var q4 = [q1[0] + normalDir[0] * (padOffset + padThick), q1[1] + normalDir[1] * (padOffset + padThick)];
        return [q1o, q2o, q3, q4];
      }

      var edgeLenPx = Math.sqrt(Math.pow(frPtB[0] - flPtB[0], 2) + Math.pow(frPtB[1] - flPtB[1], 2));

      if (state.antislip === 'velcro') {
        var strips = 5, gapPx = edgeLenPx / strips;
        for (var vi = 0; vi < strips; vi++) {
          var t0 = vi * gapPx + 3, t1 = (vi + 1) * gapPx - 3;
          if (t1 <= t0) continue;
          var quad = stripQuad(flPtB, frPtB, t0, t1);
          h += '<polygon points="' + quad.map(pt).join(' ') + '" fill="' + padC + '" opacity="' + padOp + '"/>';
        }
      } else {
        var quad = stripQuad(flPtB, frPtB, padInset, edgeLenPx - padInset);
        h += '<polygon points="' + quad.map(pt).join(' ') + '" fill="' + padC + '" opacity="' + padOp + '"/>';
      }
    }

    /* ── Dimension labels (RV style — outward from centroid) ── */
    var dimC = '#1a4f78', thkC = '#3d5e3f';
    var ls = 10;
    var hug = 18;

    var centroid = [(flPt[0] + frPt[0] + blPt[0] + brPt[0]) / 4, (flPt[1] + frPt[1] + blPt[1] + brPt[1]) / 4];
    function outward(p1, p2) {
      var mid = [(p1[0] + p2[0]) / 2, (p1[1] + p2[1]) / 2];
      var vx = mid[0] - centroid[0], vy = mid[1] - centroid[1];
      var len = Math.sqrt(vx * vx + vy * vy) || 1;
      return [vx / len, vy / len];
    }

    var lblFL = fL ? ' — ' + fL + '″' : '';
    var lblBL = bkL ? ' — ' + bkL + '″' : '';
    var lblDp = dp ? ' — ' + dp + '″' : '';
    var lblTk = tk ? ' — ' + tk + '″' : '';

    // Front Length — below front bottom edge
    var oFL = outward(flPt, frPt);
    var fla1 = [flPtB[0] + oFL[0] * hug, flPtB[1] + oFL[1] * hug];
    var fla2 = [frPtB[0] + oFL[0] * hug, frPtB[1] + oFL[1] * hug];
    h += '<line x1="' + f(flPtB[0]) + '" y1="' + f(flPtB[1]) + '" x2="' + f(fla1[0]) + '" y2="' + f(fla1[1]) + '" stroke="' + dimC + '" stroke-width="0.6"/>';
    h += '<line x1="' + f(frPtB[0]) + '" y1="' + f(frPtB[1]) + '" x2="' + f(fla2[0]) + '" y2="' + f(fla2[1]) + '" stroke="' + dimC + '" stroke-width="0.6"/>';
    h += '<line x1="' + f(fla1[0]) + '" y1="' + f(fla1[1]) + '" x2="' + f(fla2[0]) + '" y2="' + f(fla2[1])
      + '" stroke="' + dimC + '" stroke-width="1.3" marker-start="url(#bw-arr)" marker-end="url(#bw-arr)"/>';
    h += '<text x="' + f((fla1[0] + fla2[0]) / 2) + '" y="' + f((fla1[1] + fla2[1]) / 2 + 14) + '" text-anchor="middle" font-size="' + ls + '" font-family="Poppins,sans-serif" font-weight="600" fill="' + dimC + '">Front Length' + lblFL + '</text>';

    // Back Length — above back edge
    var oBL = outward(blPt, brPt);
    var bla1 = [blPt[0] + oBL[0] * hug * 0.8, blPt[1] + oBL[1] * hug * 0.8];
    var bla2 = [brPt[0] + oBL[0] * hug * 0.8, brPt[1] + oBL[1] * hug * 0.8];
    h += '<line x1="' + f(blPt[0]) + '" y1="' + f(blPt[1]) + '" x2="' + f(bla1[0]) + '" y2="' + f(bla1[1]) + '" stroke="' + dimC + '" stroke-width="0.6"/>';
    h += '<line x1="' + f(brPt[0]) + '" y1="' + f(brPt[1]) + '" x2="' + f(bla2[0]) + '" y2="' + f(bla2[1]) + '" stroke="' + dimC + '" stroke-width="0.6"/>';
    h += '<line x1="' + f(bla1[0]) + '" y1="' + f(bla1[1]) + '" x2="' + f(bla2[0]) + '" y2="' + f(bla2[1])
      + '" stroke="' + dimC + '" stroke-width="1.3" marker-start="url(#bw-arr)" marker-end="url(#bw-arr)"/>';
    h += '<text x="' + f((bla1[0] + bla2[0]) / 2) + '" y="' + f((bla1[1] + bla2[1]) / 2 - 4) + '" text-anchor="middle" font-size="' + ls + '" font-family="Poppins,sans-serif" font-weight="600" fill="' + dimC + '">Back Length' + lblBL + '</text>';

    // Depth — on the top face from fMid to bMid (like reference photo)
    h += '<line x1="' + f(fMid[0]) + '" y1="' + f(fMid[1]) + '" x2="' + f(bMid[0]) + '" y2="' + f(bMid[1])
      + '" stroke="' + dimC + '" stroke-width="1.3" marker-start="url(#bw-arr)" marker-end="url(#bw-arr)"/>';
    var dpMid = [(fMid[0] + bMid[0]) / 2, (fMid[1] + bMid[1]) / 2];
    h += '<text x="' + f(dpMid[0] + 8) + '" y="' + f(dpMid[1] - 4) + '" text-anchor="start" font-size="' + ls + '" font-family="Poppins,sans-serif" font-weight="600" fill="' + dimC + '">Depth' + lblDp + '</text>';

    // Thickness — right side vertical
    var thx = frPtB[0] + hug;
    h += '<line x1="' + f(frPt[0]) + '" y1="' + f(frPt[1]) + '" x2="' + f(thx) + '" y2="' + f(frPt[1]) + '" stroke="' + thkC + '" stroke-width="0.6"/>';
    h += '<line x1="' + f(frPtB[0]) + '" y1="' + f(frPtB[1]) + '" x2="' + f(thx) + '" y2="' + f(frPtB[1]) + '" stroke="' + thkC + '" stroke-width="0.6"/>';
    h += '<line x1="' + f(thx) + '" y1="' + f(frPt[1] + 2) + '" x2="' + f(thx) + '" y2="' + f(frPtB[1] - 2) + '" stroke="' + thkC + '" stroke-width="1.3" marker-start="url(#bw-arr)" marker-end="url(#bw-arr)"/>';
    h += '<text x="' + f(thx + 5) + '" y="' + f((frPt[1] + frPtB[1]) / 2 + 4) + '" text-anchor="start" font-size="' + ls + '" font-family="Poppins,sans-serif" font-weight="600" fill="' + thkC + '">T' + lblTk + '</text>';

    if (empty) {
      var hintX = (flPtB[0] + frPtB[0]) / 2;
      var hintY = Math.max(flPtB[1], frPtB[1]) + 38;
      h += '<text x="' + f(hintX) + '" y="' + f(hintY) + '" text-anchor="middle" font-size="10.5" font-family="Poppins,sans-serif" fill="#8A8A85">Enter dimensions below to see your cushion</text>';
    }

    svg.innerHTML = h;
  }

  /* ─── Summary ─── */
  function updateSummary() {
    if (!els.summary) return;
    var parts = [];
    if (state.frontL && state.backL && state.depth && state.thickness)
      parts.push(state.frontL + '×' + state.backL + '×' + state.depth + '×' + state.thickness + '″');
    if (state.fabric) parts.push(state.fabric.title);
    if (state.piping && state.piping !== 'none') {
      var pipLabel = LABELS[state.piping];
      if (state.pipingColor && state.pipingColor !== 'match') pipLabel += ' (' + (LABELS[state.pipingColor] || state.pipingColor) + ')';
      parts.push(pipLabel);
    }
    if (state.straps && state.straps !== 'none') parts.push(LABELS[state.straps]);
    if (state.antislip && state.antislip !== 'none') parts.push(LABELS[state.antislip]);
    els.summary.textContent = parts.length ? parts.join(' · ') : 'Configure your bay window cushion below';
  }

  /* ─── Recalc ─── */
  function recalc() {
    var _cf = window.__ucfs_selectedFabric || window._ucfsSelected;
    if (_cf && _cf !== state.fabric) state.fabric = _cf;
    state.frontL = parseFloat(els.frontL.value) || 0;
    state.backL = parseFloat(els.backL.value) || 0;
    state.depth = parseFloat(els.depth.value) || 0;
    state.thickness = parseFloat(els.thickness.value) || 0;
    state.extraCovers = els.extraCovers.value;
    state.throwPillow = els.throwPillow ? els.throwPillow.value : 'none';
    state.pillowCovers = els.pillowCovers ? els.pillowCovers.value : 'none';
    state.armrestPillow = els.armrestPillow ? els.armrestPillow.value : 'none';
    state.zipperPlacement = els.zipperPlacement ? els.zipperPlacement.value : '';
    state.pipingColor = els.pipingColor ? els.pipingColor.value : 'match';
    state.notes = els.notes.value;

    if (els.pipingColorWrap) {
      var showPipingColor = (state.piping === 'single' || state.piping === 'double');
      els.pipingColorWrap.style.display = showPipingColor ? '' : 'none';
      if (!showPipingColor) {
        state.pipingColor = 'match';
        if (els.pipingColor) els.pipingColor.value = 'match';
      }
    }

    ['frontL', 'backL', 'depth', 'thickness'].forEach(function (id) {
      els[id].classList.toggle('ok', parseFloat(els[id].value) > 0);
    });

    drawCushion();
    updateSummary();

    var dimOk = hasDims();
    var ec = parseInt(state.extraCovers) || 0;

    // Effective length = average of front and back for trapezoid
    var effectiveL = Math.max(state.frontL, state.backL);
    updateExtraCoverLabels();
    var base = dimOk ? calcBasePrice(effectiveL, state.depth, state.thickness) : null;
    var addonTotal = getAddonTotal();

    if (base !== null) {
      var finalPrice = Math.round(base + addonTotal);
      els.priceMain.textContent = '$' + finalPrice;
      var parts = ['Base $' + Math.round(base)];
      if (ADDON.piping[state.piping])     parts.push('Piping +$' + ADDON.piping[state.piping]);
      if (ADDON.pipingColor[state.pipingColor]) parts.push('Piping color +$' + ADDON.pipingColor[state.pipingColor]);
      if (ADDON.straps[state.straps])     parts.push('Straps +$' + ADDON.straps[state.straps]);
      if (ADDON.zipper[state.zipper])     parts.push('Zipper +$' + ADDON.zipper[state.zipper]);
      if (ADDON.antislip[state.antislip]) parts.push('Anti-slip +$' + ADDON.antislip[state.antislip]);
      if (ec > 0)                         parts.push('Covers +$' + (extraCoverUnit() * ec));
      if (ADDON.throwPillow[state.throwPillow])   parts.push('Pillow +$' + ADDON.throwPillow[state.throwPillow]);
      if (ADDON.pillowCovers[state.pillowCovers]) parts.push('P.Covers +$' + ADDON.pillowCovers[state.pillowCovers]);
      if (ADDON.armrestPillow[state.armrestPillow]) parts.push('Armrest +$' + ADDON.armrestPillow[state.armrestPillow]);
      els.priceBreak.textContent = parts.join(' · ');
    } else {
      els.priceMain.textContent = '$—';
      els.priceBreak.textContent = 'Enter dimensions to see price';
    }

    var complete = isComplete();
    var btns = [els.addBtn, els.addBtnSticky].filter(Boolean);
    btns.forEach(function (btn) {
      btn.disabled = !complete;
      if (complete && base !== null) {
        var fp = Math.round(base + addonTotal);
        btn.textContent = 'Add to order — $' + fp + ' · Free shipping →';
      } else {
        btn.textContent = 'Add to order →';
      }
    });

    if (els.addNote) {
      if (complete) {
        els.addNote.textContent = 'Free shipping · Ships in ~20 business days';
      } else {
        var missing = [];
        if (!dimOk) missing.push('dimensions');
        if (!state.fabric) missing.push('fabric');
        if (!state.piping) missing.push('piping');
        if (!state.straps) missing.push('straps');
        if (!state.antislip) missing.push('anti-slip');
        els.addNote.textContent = missing.length ? 'Select ' + missing.join(', ') + ' to continue' : '';
      }
    }

    document.dispatchEvent(new CustomEvent('configurator:price-updated', {
      detail: {
        price: base !== null ? Math.round(base + addonTotal) : null,
        state: state
      }
    }));
  }

  /* ─── Card selection ─── */
  function pickCard(el, group, value) {
    el.parentElement.querySelectorAll('.cc-opt-card').forEach(function (c) { c.classList.remove('sel'); });
    el.classList.add('sel');
    state[group] = value;
    recalc();
  }

  /* ─── Line properties for cart ─── */
  function getLineProperties() {
    var ec = parseInt(state.extraCovers) || 0;
    var effectiveL = Math.max(state.frontL, state.backL);
    var base = calcBasePrice(effectiveL, state.depth, state.thickness);
    var finalPrice = Math.round(base + getAddonTotal());
    var props = {
      'Shape': 'Bay Window (Trapezoid)',
      'Front Length': state.frontL + '"',
      'Back Length': state.backL + '"',
      'Depth': state.depth + '"',
      'Thickness': state.thickness + '"',
      'Piping': LABELS[state.piping] || state.piping,
      'Piping Color': (state.pipingColor && state.pipingColor !== 'match') ? (LABELS[state.pipingColor] || state.pipingColor) : 'Match fabric',
      'Straps': LABELS[state.straps] || state.straps,
      'Zipper': LABELS[state.zipper] || state.zipper,
      'Anti-slip': LABELS[state.antislip] || state.antislip
    };
    if (state.fabric) {
      props['Fabric'] = state.fabric.title || '';
      props['_Fabric SKU'] = (state.fabric.variants && state.fabric.variants[0] && state.fabric.variants[0].sku) || '';
      props['_Fabric Handle'] = state.fabric.handle || '';
      props['_Fabric Image'] = (state.fabric.images && state.fabric.images[0] && state.fabric.images[0].src) || '';
      var _grade = extractTag(state.fabric.tags || [], 'grade');
      if (_grade) props['Fabric Grade'] = _grade.charAt(0).toUpperCase() + _grade.slice(1);
    }
    if (ec > 0) props['Extra Covers'] = ec + ' set(s) (+$' + (extraCoverUnit() * ec) + ')';
    if (state.throwPillow && state.throwPillow !== 'none')
      props['Throw Pillow'] = state.throwPillow.replace(/_/g, ' ') + ' (+$' + ADDON.throwPillow[state.throwPillow] + ')';
    if (state.pillowCovers && state.pillowCovers !== 'none')
      props['Pillow Covers'] = state.pillowCovers.replace(/_/g, ' ') + ' (+$' + ADDON.pillowCovers[state.pillowCovers] + ')';
    if (state.armrestPillow && state.armrestPillow !== 'none')
      props['Armrest Pillow'] = state.armrestPillow.replace(/_/g, ' ') + ' (+$' + ADDON.armrestPillow[state.armrestPillow] + ')';
    if (state.zipperPlacement) {
      props['Zipper Placement'] = state.zipperPlacement;
    }
    if (state.notes.trim()) props['Customer Note'] = state.notes.trim();
    props['_Source Product'] = (document.getElementById('cc-configurator') || { dataset: {} }).dataset.productTitle || document.title.split('–')[0].split('—')[0].trim() || location.pathname;

    return { properties: props, price: finalPrice };
  }

  /* ─── DOM transformation (adapt shared Liquid section for Bay Window) ─── */
  function transformDOM() {
    var svg = document.getElementById('cc-dim-svg');
    if (svg) {
      svg.style.maxHeight = '240px';
    }

    // Hide Shape section entirely — bay window is always a trapezoid
    var shapeSection = document.getElementById('cc-shape-section');
    if (shapeSection) shapeSection.style.display = 'none';

    // Rename seat dimension labels for bay window
    var labelL = document.querySelector('label[for="cc-dim-l"]');
    if (labelL) labelL.innerHTML = 'Front Length (in) <span class="cc-req">*</span>';
    var inputL = document.getElementById('cc-dim-l');
    if (inputL) { inputL.placeholder = 'e.g. 72'; inputL.max = '200'; }

    var labelW = document.querySelector('label[for="cc-dim-w"]');
    if (labelW) labelW.innerHTML = 'Depth (in) <span class="cc-req">*</span>';
    var inputW = document.getElementById('cc-dim-w');
    if (inputW) inputW.placeholder = 'e.g. 18';

    var labelT = document.querySelector('label[for="cc-dim-t"]');
    if (labelT) labelT.innerHTML = 'Thickness (in) <span class="cc-req">*</span>';

    // Show back dims row for Back Length input
    var backDims = document.querySelectorAll('.cc-back-dims');
    for (var i = 0; i < backDims.length; i++) {
      var el = backDims[i];
      el.style.display = el.classList.contains('cc-sp-dims') ? 'grid' : 'block';
    }

    // Rename back label
    var backLabel = document.querySelector('.cc-back-label');
    if (backLabel) backLabel.style.display = 'none';

    // Rename back length label
    var backLLabel = document.querySelector('label[for="cc-back-l"]');
    if (backLLabel) backLLabel.innerHTML = 'Back Length (in) <span class="cc-req">*</span>';
    var inputBL = document.getElementById('cc-back-l');
    if (inputBL) { inputBL.placeholder = 'e.g. 48'; inputBL.max = '200'; }

    // Hide back width & thickness inputs (not needed for bay window)
    var backW = document.getElementById('cc-back-w');
    var backT = document.getElementById('cc-back-t');
    if (backW) backW.closest('.cc-field').style.display = 'none';
    if (backT) backT.closest('.cc-field').style.display = 'none';


    // Hide price area above SVG (show price on button instead)
    var spTop = document.querySelector('.cc-sp-top');
    if (spTop) spTop.style.display = 'none';
    var priceBreakDiv = document.getElementById('cc-price-break');
    if (priceBreakDiv) priceBreakDiv.style.display = 'none';

    // Tighten label spacing
    var dimsLabels = document.querySelectorAll('.cc-dims-label');
    for (var j = 0; j < dimsLabels.length; j++) {
      dimsLabels[j].style.marginBottom = '0';
      dimsLabels[j].style.paddingBottom = '0';
    }

    // Move dimension inputs out of sticky container
    var stickyWrap = document.getElementById('cc-sticky-preview');
    if (stickyWrap) {
      var dimsContainer = document.createElement('div');
      dimsContainer.className = 'cc-dims-outside';

      var seatDims = stickyWrap.querySelector('.cc-sp-dims:not(.cc-back-dims)');
      var backDimsEl = stickyWrap.querySelector('.cc-sp-dims.cc-back-dims');
      var hint = stickyWrap.querySelector('.cc-sp-hint');

      if (seatDims) dimsContainer.appendChild(seatDims);
      if (backDimsEl) dimsContainer.appendChild(backDimsEl);
      if (hint) dimsContainer.appendChild(hint);

      stickyWrap.parentNode.insertBefore(dimsContainer, stickyWrap.nextSibling);
    }

    // Renumber steps since Shape (step 1) is hidden
    var secHeads = document.querySelectorAll('.cc-sec-head .cc-sn');
    var stepNum = 1;
    for (var k = 0; k < secHeads.length; k++) {
      var sec = secHeads[k].closest('.cc-sec');
      if (sec && sec.style.display !== 'none') {
        secHeads[k].textContent = stepNum++;
      }
    }
  }

  /* ─── Init ─── */
  function init() {
    var wrap = document.getElementById('cc-configurator');
    if (!wrap) return;

    transformDOM();

    els.priceMain    = document.getElementById('cc-price-main');
    els.priceBreak   = document.getElementById('cc-price-break');
    els.summary      = document.getElementById('cc-sp-summary');
    els.frontL       = document.getElementById('cc-dim-l');
    els.depth        = document.getElementById('cc-dim-w');
    els.thickness    = document.getElementById('cc-dim-t');
    els.backL        = document.getElementById('cc-back-l');
    els.extraCovers  = document.getElementById('cc-extra-covers');
    els.throwPillow  = document.getElementById('cc-throw-pillow');
    els.pillowCovers = document.getElementById('cc-pillow-covers');
    els.armrestPillow = document.getElementById('cc-armrest-pillow');
    els.zipperPlacement = document.getElementById('cc-zipper-placement');
    els.pipingColor    = document.getElementById('cc-piping-color');
    els.pipingColorWrap = document.getElementById('cc-piping-color-wrap');
    els.notes        = document.getElementById('cc-notes');
    els.addBtn       = document.getElementById('cc-add-btn');
    els.addBtnSticky = document.getElementById('cc-add-btn-sticky');
    els.addNote      = document.getElementById('cc-add-note');
    els.dimSvg       = document.getElementById('cc-dim-svg');

    wrap.addEventListener('click', function (e) {
      var card = e.target.closest('.cc-opt-card');
      if (!card || !card.dataset.group) return;
      pickCard(card, card.dataset.group, card.dataset.value);
    });

    ['frontL', 'depth', 'thickness', 'backL'].forEach(function (id) {
      if (els[id]) els[id].addEventListener('input', recalc);
    });
    els.extraCovers.addEventListener('change', recalc);
    if (els.throwPillow) els.throwPillow.addEventListener('change', recalc);
    if (els.pillowCovers) els.pillowCovers.addEventListener('change', recalc);
    if (els.armrestPillow) els.armrestPillow.addEventListener('change', recalc);
    if (els.zipperPlacement) els.zipperPlacement.addEventListener('change', recalc);
    if (els.pipingColor) els.pipingColor.addEventListener('change', recalc);
    els.notes.addEventListener('input', recalc);

    document.addEventListener('fabric:selected', function (e) {
      state.fabric = e.detail;
      recalc();
    });
    var existingFabric = window.__ucfs_selectedFabric || window._ucfsSelected;
    if (!state.fabric && existingFabric) {
      state.fabric = existingFabric;
      recalc();
    }
    setInterval(function () {
      var f = window.__ucfs_selectedFabric || window._ucfsSelected;
      if (f && f !== state.fabric) {
        state.fabric = f;
        recalc();
      }
    }, 800);

    function handleAdd() {
      if (!isComplete()) return;
      document.dispatchEvent(new CustomEvent('configurator:add-to-cart', { detail: getLineProperties() }));
    }
    if (els.addBtn) els.addBtn.addEventListener('click', handleAdd);
    if (els.addBtnSticky) els.addBtnSticky.addEventListener('click', handleAdd);

    var defaultZipper = wrap.querySelector('.cc-opt-card[data-group="zipper"][data-value="regular"]');
    if (defaultZipper) defaultZipper.classList.add('sel');

    drawCushion();
    recalc();
  }

  window.BayWindowCushionConfigurator = {
    getState: function () { return state; },
    getLineProperties: getLineProperties,
    recalc: recalc
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
